/**
 * Hero background videos are never loaded before the page has painted.
 *
 * The poster rendered by hero-background.php is a real <img> with
 * fetchpriority="high" and is the LCP element on every viewport. The mp4 is
 * attached only once `load` has fired and the main thread is idle, so it never
 * competes with the poster, the stylesheet or the fonts for the connection -
 * a hero video fetched during the critical path is what used to delay the
 * largest paint by seconds. `preload="none"` in the markup keeps the browser
 * from touching the file on its own until then.
 *
 * In a slider only the visible slide's video is fetched, and the rest follow as
 * they are shown. Clients that asked for reduced data keep the poster. Without
 * JS the <noscript> copy in hero-background.php takes over.
 *
 * @see ../../../site/snippets/hero/hero-background.php
 */
const SLIDE = ".hero-slider__slide";

/**
 * A hero slider stacks every slide on top of the other and shows one at a time,
 * so only the active one is worth several megabytes. A video outside a slider
 * has no slide ancestor and is always on.
 */
function isVisible(video) {
	const slide = video.closest(SLIDE);
	return slide === null || slide.classList.contains("is-active");
}

/**
 * Data Saver in Chrome/Android, and what a metered connection maps to. The
 * video is decoration over a poster that is already on screen, so honouring it
 * costs the visitor nothing.
 */
function savesData() {
	return navigator.connection?.saveData === true;
}

function attach(video) {
	if (!video.dataset.src || video.querySelector("source")) {
		return;
	}

	const container = video.closest(".bgvideo");
	const reveal = () => container?.classList.add("is-playing");

	// `playing` covers the normal case, `loadeddata` the one where autoplay was
	// refused - the first frame is decoded either way, so the fade may run.
	video.addEventListener("playing", reveal, { once: true });
	video.addEventListener("loadeddata", reveal, { once: true });

	const source = document.createElement("source");
	source.src = video.dataset.src;
	source.type = "video/mp4";

	video.appendChild(source);
	video.load();

	// Autoplay can be refused (power saving, reduced data); the poster stays.
	const playing = video.play();
	if (playing) {
		playing.catch(() => {});
	}
}

function init() {
	const videos = document.querySelectorAll("video[data-hero-video]");

	if (videos.length === 0 || savesData()) {
		return;
	}

	const sync = () => {
		videos.forEach((video) => {
			if (isVisible(video)) {
				attach(video);
			}
		});
	};

	// The slider swaps `is-active` between slides; watching the attribute keeps
	// this out of the slider's own code, which lives in the legacy bundle.
	const slides = document.querySelectorAll(SLIDE);

	if (slides.length > 0 && "MutationObserver" in window) {
		const observer = new MutationObserver(sync);
		slides.forEach((slide) =>
			observer.observe(slide, {
				attributes: true,
				attributeFilter: ["class"],
			}),
		);
	}

	// Once the rest of the page has loaded the mp4 no longer competes with the
	// poster, the stylesheet or the fonts for the connection.
	const start = () => {
		if ("requestIdleCallback" in window) {
			window.requestIdleCallback(sync, { timeout: 2000 });
		} else {
			window.setTimeout(sync, 200);
		}
	};

	if (document.readyState === "complete") {
		start();
	} else {
		window.addEventListener("load", start, { once: true });
	}
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", init);
} else {
	init();
}
